import { FCX, includesInLC, startsFromInLC } from "@datassential/platform-ui-lib";
import { useTrendSummaryTableConfig } from "@pages/Trend/components/TrendSummary/TrendSummaryTableConfig";
import { TableColumnsContext } from "@components/Table/TableColumns";
import { useId, useMemo } from "react";
import { VirtualizedTable } from "@components/Table";
import { TrendSummaryItem, useTrendSummary } from "@api/useTrendSummary";
import { useTrendContext } from "@pages/Trend/TrendContext";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import SearchFilters from "@pages/Trend/components/SearchFilters";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import HeatScoreTooltip from "@vms/HeatScoreTooltip";
import Loader from "@components/Loader";
import { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";

const TrendSummary: FCX<{
    isVisible: boolean;
}> = ({
    className,
    isVisible,
    ...props
}) => {
    const heatScoreTooltipId = useId();
    const { search } = useTrendContext();
    const { getMatchFilters } = useSelectFilters();

    const { amongPayload } = useSelectAmongContext();

    const { data: rawData, isLoading } = useTrendSummary(amongPayload);

    const isSearchApplied = useMemo(
        () => search.trim().length > 0,
        [search]
    );

    const preSortedData = useMemo(
        () => [...rawData].sort(
            (a, b) => (a.name ?? '').localeCompare(b.name ?? '')
        ),
        [rawData]
    );

    const filteredData = useMemo(
        () => preSortedData.filter(
            i => {
                let result = true;

                if (isSearchApplied) {
                    result = includesInLC(i.name ?? '', search.trim());
                }

                result = result && getMatchFilters(i);

                return result;
            }
        ),
        [preSortedData, isSearchApplied, search, getMatchFilters]
    );

    const dataForSorting = useMemo(
        () => (filteredData || []).map(i => ({
            ...i,
            ...i.metrics.reduce(
                (acc, metric, index) => ({
                    ...acc,
                    [`metric${index}`]: metric.numValue,
                    [`metric${index}Change`]: metric.changeNumValue,
                }),
                {}
            ),
        })),
        [filteredData]
    );

    const sortedBySearch = useMemo(
        () => {
            if (!isSearchApplied) return filteredData;

            return [
                ...filteredData.filter(i => startsFromInLC(i.name ?? '', search.trim())),
                ...filteredData.filter(i => !startsFromInLC(i.name ?? '', search.trim())),
            ];
        },
        [filteredData, isSearchApplied, search]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        dataForSorting,
        'name',
        Sort.Asc,
        ['name']
    );

    const data = useMemo(
        () => isSearchApplied ? sortedBySearch : sortedData,
        [sortedBySearch, sortedData, isSearchApplied]
    );

    const sortContextValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: "name", name: "name", defaultDirection: Sort.Asc },
                { id: "macStage", name: "macStage", defaultDirection: Sort.Asc },
                { id: "baseCount", name: "baseCount", },
                { id: "metric0", name: "metric0", },
                { id: "metric0Change", name: "metric0Change", },
                { id: "metric1", name: "metric1", },
                { id: "metric1Change", name: "metric1Change", },
                { id: "metric2", name: "metric2", },
                { id: "metric2Change", name: "metric2Change", },
                { id: "metric3", name: "metric3", },
                { id: "metric3Change", name: "metric3Change", },
                { id: "heatScoreNum", name: "heatScoreNum" },
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof TrendSummaryItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const tableConfig = useTrendSummaryTableConfig(data, heatScoreTooltipId, isSearchApplied);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length);

    const tableKey = useMemo(() => {
        return `${data.length}-${Date.now()}`;
    }, [data]);

    if (!isVisible) {
        return null;
    }

    if (isLoading) {
        return (
            <Loader/>
        );
    }

    return (
        <div
            className={`TrendSummary ${className}`}
            {...props}
            data-testid="blockTrendSummary"
        >
            <SearchFilters className="appears-top"/>
            <TableSortingContext.Provider value={sortContextValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <div className="TrendSummary__fixed-table appears-top del-2">
                        <AsideButtons>
                            <CopyTableButton
                                data={copyTableContent}
                                testId="buttonProfileCGCopyTable"
                            />
                        </AsideButtons>
                        <VirtualizedTable
                            key={tableKey}
                            rowsCount={data.length}
                            headHeight={52}
                            rowHeight={42}
                            showFallback
                        />
                        <HeatScoreTooltip id={heatScoreTooltipId}/>
                    </div>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
        </div>
    );
};

export default TrendSummary;